import type { UseFormMethods } from 'react-hook-form';

import { Intl } from '@src/_metronic/i18n/I18nProvider';

import type { Consent } from '@app/store/customers/customers.store';

import { GDPR_FIELDS } from './Customer';

type GdprValue = Consent['gdprEmail'] | undefined;

type GdprField = { slug: string; map: string; label: string };

type GdprSwitchOptions = {
    form: Pick<UseFormMethods, 'watch' | 'getValues' | 'setValue'>;
    getFieldName: (gdprField: GdprField) => string;
    getInitialValue: (gdprField: GdprField) => GdprValue;
};

type GdprSwitchProps = {
    active: GdprValue;
    disabled?: boolean;
    onChange?: (value: GdprValue) => void;
    hint?: string;
};

export const GDPR_EMAIL_SLUG = 'gdprEmail';
export const GDPR_PIXEL_SLUG = 'gdprPixel';

const getGdprField = (slug: string): GdprField => (GDPR_FIELDS as GdprField[]).find((field) => field.slug === slug)!;

export const isGdprEmailGranted = (email: GdprValue) => email === true;

export const getAllowedGdprPixel = <T extends GdprValue>(email: GdprValue, pixel: T): T | false =>
    pixel === true && !isGdprEmailGranted(email) ? false : pixel;

export const getGdprSwitchProps = (
    gdprField: GdprField,
    { form, getFieldName, getInitialValue }: GdprSwitchOptions,
): GdprSwitchProps => {
    const initialValue = getInitialValue(gdprField);
    const emailField = getGdprField(GDPR_EMAIL_SLUG);
    const pixelField = getGdprField(GDPR_PIXEL_SLUG);

    if (gdprField.slug === GDPR_EMAIL_SLUG) {
        return {
            active: initialValue,
            onChange: (value) => {
                const pixelName = getFieldName(pixelField);
                form.setValue(pixelName, getAllowedGdprPixel(value, form.getValues(pixelName)));
            },
        };
    }

    if (gdprField.slug === GDPR_PIXEL_SLUG) {
        const initialEmail = getInitialValue(emailField);
        const disabled = !isGdprEmailGranted(form.watch(getFieldName(emailField), initialEmail));

        return {
            // An already stored Pixel consent without E-mail consent is invalid, so it is reset to false.
            active: getAllowedGdprPixel(initialEmail, initialValue),
            disabled,
            hint: disabled ? Intl.formatMessage({ id: 'TRANSLATOR.CONSENT_PIXEL_REQUIRES_EMAIL' }) : undefined,
        };
    }

    return { active: initialValue };
};
